import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, DatePicker, message, Tooltip, Input, Select, Card, Row, Col, Statistic, Typography, Spin } from 'antd';
import { EyeOutlined, FileTextOutlined, ReloadOutlined, CreditCardOutlined, SearchOutlined, DollarOutlined, FileProtectOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getCreditNoteHistory } from '../services/creditNoteService';
import { useAuth } from '../context/AuthContext';
import { isAdminRole } from '../permissions';
import './creditNotesPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CreditNotesPage = () => {
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const [creditNotes, setCreditNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // Summary state
    const [summary, setSummary] = useState({
        totalCount: 0,
        countByType: {
            notaA: 0,
            notaB: 0,
            notaC: 0,
            notaE: 0,
            notaM: 0
        },
        countByCancellationType: {
            full: 0,
            partial: 0
        },
        currentMonth: {
            totalSum: 0,
            count: 0
        }
    });

    // Filters
    const [filters, setFilters] = useState({
        id: '',
        numeroComprobante: '',
        invoiceNumeroComprobante: '',
        clienteRazonSocial: '',
        tipoComprobante: '',
        cancellationType: '',
        dateFrom: null,
        dateTo: null,
    });

    // Fetch credit notes from backend
    const fetchCreditNotes = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
            };

            // Add all filters to params
            if (filters.id) params.id = filters.id;
            if (filters.numeroComprobante) params.numeroComprobante = filters.numeroComprobante;
            if (filters.invoiceNumeroComprobante) params.invoiceNumeroComprobante = filters.invoiceNumeroComprobante;
            if (filters.clienteRazonSocial) params.clienteRazonSocial = filters.clienteRazonSocial;
            if (filters.tipoComprobante) params.tipoComprobante = filters.tipoComprobante;
            if (filters.cancellationType) params.cancellationType = filters.cancellationType;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo) params.dateTo = filters.dateTo;

            const response = await getCreditNoteHistory(params);

            if (response && response.success) {
                const creditNotes = response.data.creditNotes || [];
                
                setCreditNotes(creditNotes);
                setPagination({
                    current: response.data.pagination?.page || page,
                    pageSize: response.data.pagination?.limit || pageSize,
                    total: response.data.pagination?.total || 0,
                });

                // Store summary data
                if (response.data.summary) {
                    setSummary(response.data.summary);
                }
            } else {
                message.error('Error al cargar notas de crédito');
            }
        } catch (error) {
            console.error('Error fetching credit notes:', error);
            message.error('Error al cargar notas de crédito del servidor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCreditNotes();
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        fetchCreditNotes(paginationConfig.current, paginationConfig.pageSize);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Handle filter change with auto-reload when cleared
    const handleFilterChangeWithReload = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        // If value is empty/null (filter cleared), reload data
        if (!value || value === '') {
            setTimeout(() => fetchCreditNotes(1, pagination.pageSize), 50);
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        fetchCreditNotes(1, pagination.pageSize);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            id: '',
            numeroComprobante: '',
            invoiceNumeroComprobante: '',
            clienteRazonSocial: '',
            tipoComprobante: '',
            cancellationType: '',
            dateFrom: null,
            dateTo: null,
        });
        setTimeout(() => fetchCreditNotes(1, pagination.pageSize), 100);
    };

    // Handle Enter key to apply filters
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    // View credit note details
    const handleViewCreditNote = async (record) => {
        try {
            message.loading({ content: 'Cargando nota de crédito...', key: 'loadCreditNote', duration: 0 });
            
            // Import credit note service
            const { getCreditNote } = await import('../services/creditNoteService');
            
            // Fetch full credit note data with items from backend
            const response = await getCreditNote(record.id);
            
            if (response && response.success && response.creditNote) {
                // Store complete credit note data in sessionStorage for print page
                sessionStorage.setItem('printCreditNoteData', JSON.stringify(response.creditNote));
                message.success({ content: 'Nota de crédito cargada', key: 'loadCreditNote', duration: 1 });
                // Open credit note in print-quote page
                window.open(`#/print-quote?creditNoteId=${record.id}`, '_blank');
            } else {
                // If API fails, store the record data as fallback
                sessionStorage.setItem('printCreditNoteData', JSON.stringify(record));
                message.warning({ content: 'Mostrando datos básicos de la nota de crédito', key: 'loadCreditNote', duration: 2 });
                // Open credit note in print-quote page anyway
                window.open(`#/print-quote?creditNoteId=${record.id}`, '_blank');
            }
        } catch (error) {
            console.error('Error loading credit note:', error);
            // Store the record as fallback
            sessionStorage.setItem('printCreditNoteData', JSON.stringify(record));
            message.warning({ content: 'Mostrando datos básicos de la nota de crédito', key: 'loadCreditNote', duration: 2 });
            // Open credit note in print-quote page anyway
            window.open(`#/print-quote?creditNoteId=${record.id}`, '_blank');
        }
    };

    // View original invoice (the invoice that was cancelled)
    const handleViewOriginalInvoice = (invoiceId) => {
        window.open(`#/print-quote?invoiceId=${invoiceId}`, '_blank');
    };

    // Format price
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusColors = {
            'approved': 'green',
            'error': 'red',
            'pending': 'orange',
            'processing': 'gold',
            'draft': 'blue',
        };
        return statusColors[status] || 'default';
    };

    // Table columns
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'N. Crédito',
            dataIndex: 'numero_comprobante',
            key: 'numero_comprobante',
            width: 100,
            render: (numero, record) => {
                if (numero) {
                    // Remove first 6 characters (e.g., "00005-") for NC A/B
                    const displayNumber = numero.length > 6 ? numero.substring(6) : numero;
                    return <strong>{displayNumber}</strong>;
                }
                // Fallback for NC X (internal)
                return <span style={{ color: '#999' }}>NC-{record.id}</span>;
            },
        },
        {
            title: 'F. Original',
            key: 'factura_original',
            width: 100,
            render: (_, record) => {
                const hasAFIPNumber = record.invoice_numero_comprobante;
                
                if (hasAFIPNumber) {
                    // Remove first 6 characters (e.g., "00005-") for NC A/B invoices
                    const displayNumber = hasAFIPNumber.length > 6 ? hasAFIPNumber.substring(6) : hasAFIPNumber;
                    return <strong>{displayNumber}</strong>;
                }
                // Fallback to invoice ID for Presupuestos (NC X)
                return (
                    <span style={{ color: '#666' }}>
                        ID #{record.original_invoice_id}
                    </span>
                );
            },
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo_comprobante',
            key: 'tipo_comprobante',
            width: 165,
            render: (tipo, record) => {
                const tipoStr = tipo || 'NOTA DE CRÉDITO';
                const isInternal = !record.cae && !record.numero_comprobante; // NC C
                
                let color = 'default';
                
                // Check for "B" before "A" to avoid false matches
                // Replace NC C (internal) with NC X
                let displayTipo = tipoStr;
                if (isInternal) {
                    displayTipo = 'NOTA DE CREDITO X';
                    color = 'default';
                } else if (tipoStr.includes('NOTA DE CREDITO B') || tipoStr.endsWith(' B')) {
                    color = 'orange';
                } else if (tipoStr.includes('NOTA DE CREDITO A') || tipoStr.endsWith(' A')) {
                    color = 'green';
                }
                
                return <Tag color={color}>{displayTipo}</Tag>;
            },
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const colors = {
                    'approved': 'green',
                    'pending': 'gold',
                    'failed': 'red',
                    'error': 'red',
                };
                const labels = {
                    'approved': 'Aprobada',
                    'pending': 'Pendiente',
                    'failed': 'Error',
                    'error': 'Error',
                };
                return (
                    <Tag color={colors[status] || 'default'}>
                        {labels[status] || status}
                    </Tag>
                );
            },
        },
        {
            title: 'Anulación',
            dataIndex: 'cancellation_type',
            key: 'cancellation_type',
            width: 100,
            render: (type) => {
                if (type === 'partial') {
                    return <Tag color="warning">Parcial</Tag>;
                } else if (type === 'full') {
                    return <Tag color="blue">Completa</Tag>;
                } else {
                    return <Tag color="default">Completa</Tag>;
                }
            },
        },
        {
            title: 'Cliente',
            dataIndex: 'cliente_razon_social',
            key: 'cliente_razon_social',
            ellipsis: true,
            width: 180,
            render: (text) => text || 'Consumidor Final',
        },
        {
            title: 'Fecha',
            dataIndex: 'fecha_emision',
            key: 'fecha_emision',
            width: 110,
            render: (date) => {
                if (!date) return 'N/A';
                const dateStr = String(date).split('T')[0];
                const [year, month, day] = dateStr.split('-');
                return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
            },
        },
        {
            title: 'Subtotal',
            dataIndex: 'subtotal',
            key: 'subtotal',
            width: 110,
            align: 'right',
            render: (subtotal, record) => {
                const isNCA = record.tipo_comprobante === 'NOTA DE CREDITO A';
                // Only show for NC A (neto gravado - discriminated)
                if (isNCA && subtotal) {
                    return formatPrice(subtotal);
                }
                return <span style={{ color: '#999' }}>—</span>;
            },
        },
        {
            title: 'IVA',
            key: 'iva',
            width: 110,
            align: 'right',
            render: (_, record) => {
                const isNCA = record.tipo_comprobante === 'NOTA DE CREDITO A';
                // Only show for NC A (IVA discriminated)
                if (isNCA && record.subtotal && record.total) {
                    const iva = record.total - record.subtotal;
                    return formatPrice(iva);
                }
                return <span style={{ color: '#999' }}>—</span>;
            },
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 110,
            align: 'right',
            render: (total) => <strong>{formatPrice(total)}</strong>,
        },
        {
            title: 'CAE',
            dataIndex: 'cae',
            key: 'cae',
            width: 140,
            render: (cae, record) => {
                if (!cae) {
                    const isInternal = !record.cae && !record.numero_comprobante;
                    if (isInternal) {
                        return <span style={{ color: '#999' }}>Sin CAE</span>;
                    }
                    return <Tag color="warning">Pendiente</Tag>;
                }
                const cleanCae = String(cae).trim();
                return (
                    <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '11px',
                        color: '#000'
                    }}>
                        {cleanCae}
                    </span>
                );
            },
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 140,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Ver detalles de la Nota de Crédito">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Vista previa de la Nota de Crédito en formato PDF con CAE y datos del comprobante'); return; }
                                handleViewCreditNote(record);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Ver Factura Anulada">
                        <Button
                            type="default"
                            size="small"
                            icon={<FileTextOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Factura original asociada a esta Nota de Crédito'); return; }
                                handleViewOriginalInvoice(record.original_invoice_id);
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="credit-notes-page-container">
            <div className="credit-notes-header">
                <h1>
                    <CreditCardOutlined />
                    Notas de Crédito
                </h1>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchCreditNotes(pagination.current, pagination.pageSize)}
                >
                    Actualizar
                </Button>
            </div>

            {/* Summary Cards — admin only */}
            {isAdmin && <div className="credit-notes-summary">
                <Row gutter={16} style={{ marginBottom: 24, display: 'flex' }}>
                    <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #1890ff', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>TOTAL NOTAS DE CRÉDITO</Typography.Text>}
                                        value={summary.totalCount}
                                        valueStyle={{ color: '#1890ff', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Notas A: <strong>{summary.countByType.notaA}</strong> | B: <strong>{summary.countByType.notaB}</strong> | X: <strong>{summary.countByType.notaC}</strong>
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #cf1322', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>MES ACTUAL</Typography.Text>}
                                        value={Math.ceil(summary.currentMonth.totalSum)}
                                        valueStyle={{ color: '#cf1322', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Total de <strong>{summary.currentMonth.count}</strong> Notas de Crédito
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #52c41a', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>ANULACIONES COMPLETAS</Typography.Text>}
                                        value={summary.countByCancellationType.full}
                                        valueStyle={{ color: '#52c41a', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Por el total del valor de Factura
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #faad14', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>ANULACIONES PARCIALES</Typography.Text>}
                                        value={summary.countByCancellationType.partial}
                                        valueStyle={{ color: '#faad14', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Por valor parcial de Factura o ajuste de NC
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>}

            {/* Filters */}
            <div className="credit-notes-filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="ID Nota de Crédito..."
                            value={filters.id}
                            onChange={(e) => handleFilterChangeWithReload('id', e.target.value)}
                            onKeyPress={handleKeyPress}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="N° Nota de Crédito..."
                            value={filters.numeroComprobante}
                            onChange={(e) => handleFilterChangeWithReload('numeroComprobante', e.target.value)}
                            onKeyPress={handleKeyPress}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="N° Factura Original..."
                            value={filters.invoiceNumeroComprobante}
                            onChange={(e) => handleFilterChangeWithReload('invoiceNumeroComprobante', e.target.value)}
                            onKeyPress={handleKeyPress}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="Buscar por Cliente..."
                            value={filters.clienteRazonSocial}
                            onChange={(e) => handleFilterChangeWithReload('clienteRazonSocial', e.target.value)}
                            onKeyPress={handleKeyPress}
                            allowClear
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Tipo de Comprobante"
                            value={filters.tipoComprobante || undefined}
                            onChange={(value) => handleFilterChangeWithReload('tipoComprobante', value)}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="NOTA DE CREDITO A">NOTA DE CREDITO A</Option>
                            <Option value="NOTA DE CREDITO B">NOTA DE CREDITO B</Option>
                            <Option value="NOTA DE CREDITO C">NOTA DE CREDITO C</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Tipo de Anulación"
                            value={filters.cancellationType || undefined}
                            onChange={(value) => handleFilterChangeWithReload('cancellationType', value)}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="full">Completa</Option>
                            <Option value="partial">Parcial</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <RangePicker
                            format="DD/MM/YYYY"
                            placeholder={['Fecha desde', 'Fecha hasta']}
                            onChange={(dates) => {
                                if (dates) {
                                    handleFilterChange('dateFrom', dates[0].format('YYYY-MM-DD'));
                                    handleFilterChange('dateTo', dates[1].format('YYYY-MM-DD'));
                                } else {
                                    handleFilterChange('dateFrom', null);
                                    handleFilterChange('dateTo', null);
                                }
                            }}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Space>
                            <Button type="primary" onClick={handleApplyFilters}>
                                Aplicar Filtros
                            </Button>
                            <Button onClick={handleClearFilters}>
                                Limpiar
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <div className="credit-notes-table-container">
                <Table
                    columns={columns}
                    dataSource={creditNotes}
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ x: 1400 }}
                    size="middle"
                />
            </div>
        </div>
    );
};

export default CreditNotesPage;
